import { Grid, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ReactECharts from "echarts-for-react";
import { DelayForPareto } from "../models/DelayForPareto";
import { paretoGraphicStyles } from "../styles/paretoGraphicStyles";

export const DelaysParetoGraphic = ({
  delays,
}: {
  delays: DelayForPareto[];
}) => {
  const theme = useTheme();
  const styles = paretoGraphicStyles(theme);

  const formatMinutes = (minutes: number) => {
    return new Intl.NumberFormat("en-US").format(minutes);
  };

  const getChartOptions = () => {
    const paretoData = delays
      .filter(
        (item) =>
          item.DelayCodeName !== null && item.DelayCodeName !== undefined,
      )
      .map((item) => ({
        ...item,
        DelayCodeName: item.DelayCodeName || "",
        DelayCategoryName: item.DelayCategoryName || "",
      }));

    // Calculate cumulative percentage data (from filtered paretoData)
    const totalMinutes = paretoData.reduce(
      (sum, item) => sum + (item.TotalDelayMinutes || 0),
      0,
    );
    let cumulative = 0;
    const cumulativeData = paretoData.map((item) => {
      cumulative += item.TotalDelayMinutes || 0;
      return parseFloat(((cumulative / totalMinutes) * 100).toFixed(1));
    });

    const options = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any) => {
          const barPoint = params.find((p: any) => p.seriesType === "bar");
          const linePoint = params.find((p: any) => p.seriesType === "line");
          const currentItem = paretoData[barPoint?.dataIndex];
          const categoryInfo = currentItem?.DelayCategoryName
            ? `<br/>Category: ${currentItem.DelayCategoryName}`
            : "";
          const cumulativePct =
            linePoint?.value !== undefined
              ? `<br/>Cumulative: ${linePoint.value}%`
              : "";

          return `${currentItem.DelayCodeName}<br/>
                  Minutes: ${formatMinutes(barPoint?.value || 0)}${cumulativePct}${categoryInfo}`;
        },
      },
      grid: {
        left: "5%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: [
        {
          type: "category",
          data: paretoData.map((item) => item.DelayCodeName),
          axisTick: {
            alignWithLabel: true,
          },
          axisLabel: {
            rotate: 45,
            interval: 0,
          },
        },
      ],
      yAxis: [
        {
          type: "value",
          name: "Minutes",
          nameLocation: "middle",
          nameGap: 50,
          splitLine: {
            show: true,
          },
          axisLine: {
            show: true,
          },
          axisTick: {
            show: true,
          },
          axisLabel: {
            show: true,
            formatter: (value: number) => {
              return formatMinutes(value);
            },
          },
        },
        {
          type: "value",
          name: "Cumulative %",
          position: "right",
          min: 0,
          max: 100,
          axisLabel: {
            formatter: "{value}%",
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: "Delay Minutes",
          type: "bar",
          barWidth: "60%",
          data: paretoData.map((item) => item.TotalDelayMinutes || 0),
          itemStyle: {
            color: theme.palette.primary.main,
          },
          label: {
            show: true,
            position: "top",
            formatter: (params: any) => {
              return formatMinutes(params.value);
            },
          },
        },
        {
          name: "Cumulative %",
          type: "line",
          yAxisIndex: 1,
          data: cumulativeData,
          itemStyle: {
            color: theme.palette.error.main,
          },
          smooth: true,
          showSymbol: false,
        },
      ],
    };

    return options;
  };

  return (
    <Grid onContextMenu={(e) => e.preventDefault()}>
      <Paper elevation={3} sx={{ ...styles.gridWrapper, marginTop: "0.5rem" }}>
        <ReactECharts
          option={getChartOptions()}
          style={{ height: "100%", width: "100%" }}
          opts={{ renderer: "svg" }}
        />
      </Paper>
    </Grid>
  );
};
